import type { DemoProfile, DemoScenario } from './types';

const img = (seed: string) => `https://picsum.photos/seed/novembr-${seed}/360/480`;

const hoursAgo = (hours: number) => new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();

const makeProfile = (
  partial: Omit<DemoProfile, 'created_at'> & { hoursAgo?: number }
): DemoProfile => {
  const { hoursAgo: ageHours = 2, ...rest } = partial;
  return {
    ...rest,
    status: rest.status ?? 'pending',
    created_at: hoursAgo(ageHours),
  };
};

const launchProfiles: DemoProfile[] = [
  makeProfile({
    id: 'demo-l-1',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'maya_2841',
    name: 'Maya Chen',
    age: 29,
    sex: 'Woman',
    occupation: 'Product designer',
    location: 'Brooklyn, NY',
    trust_score: 82,
    status: 'pending',
    hoursAgo: 0.4,
    images: [img('maya-1'), img('maya-2'), img('maya-3')],
    bio: 'Product designer in Brooklyn. Weekend hikes and too much coffee.',
    prompts: [
      { question: 'A perfect Sunday looks like', answer: 'Farmers market, long walk, then cooking with friends.' },
      { question: 'Green flag', answer: 'Asks thoughtful questions and remembers the small things.' },
    ],
    image_results: [{ passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 88 },
  }),
  makeProfile({
    id: 'demo-l-2',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'jordan_k',
    name: 'Jordan Kim',
    age: 31,
    sex: 'Man',
    occupation: 'Software engineer',
    trust_score: 54,
    status: 'pending',
    hoursAgo: 1.2,
    images: [img('jordan-1'), img('jordan-2')],
    bio: 'Engineer. Dog dad. Trying every ramen spot in the city.',
    prompts: [{ question: 'I geek out on', answer: 'Mechanical keyboards and sourdough starters.' }],
    image_results: [{ passed: true }, { passed: false }],
    text_results: { score: 61 },
  }),
  makeProfile({
    id: 'demo-l-3',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'samira_09',
    name: 'Samira Okonkwo',
    age: 27,
    sex: 'Woman',
    occupation: 'Registered nurse',
    location: 'Chicago, IL',
    trust_score: 91,
    status: 'pending',
    hoursAgo: 2.5,
    images: [img('samira-1'), img('samira-2'), img('samira-3'), img('samira-4')],
    bio: 'Nurse & marathon trainee. Looking for someone kind and curious.',
    image_results: [{ passed: true }, { passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 94 },
  }),
  makeProfile({
    id: 'demo-l-4',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'alex_demo',
    name: 'Alex',
    trust_score: 38,
    status: 'pending',
    hoursAgo: 0.8,
    images: [img('alex-1')],
    bio: 'New here. Love live music.',
    image_results: [{ passed: false }],
    text_results: { score: 42 },
  }),
  makeProfile({
    id: 'demo-l-5',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'taylor_q',
    trust_score: 67,
    status: 'pending',
    hoursAgo: 3,
    images: [img('taylor-1'), img('taylor-2')],
    bio: 'Teacher. Book club host. Always planning the next trip.',
    text_results: { score: 72 },
  }),
  makeProfile({
    id: 'demo-l-6',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'chris_v',
    trust_score: 88,
    status: 'approved',
    hoursAgo: 48,
    images: [img('chris-1'), img('chris-2')],
    bio: 'Photographer. Golden hour enthusiast.',
    image_results: [{ passed: true }, { passed: true }],
    text_results: { score: 90 },
  }),
  makeProfile({
    id: 'demo-l-7',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'nina_p',
    trust_score: 79,
    status: 'approved',
    hoursAgo: 72,
    images: [img('nina-1'), img('nina-2'), img('nina-3')],
    bio: 'Founder building community tools.',
    text_results: { score: 81 },
  }),
  makeProfile({
    id: 'demo-l-8',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'bot_cluster_44',
    name: 'Unknown',
    trust_score: 12,
    status: 'rejected',
    hoursAgo: 6,
    images: [img('bot-1')],
    bio: 'Hey!!! Add me on telegram @fake_user',
    image_results: [{ passed: false }],
    text_results: { score: 18 },
  }),
  makeProfile({
    id: 'demo-l-9',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'riley_m',
    trust_score: 22,
    status: 'rejected',
    hoursAgo: 12,
    images: [img('riley-1'), img('riley-2')],
    bio: 'Click link in bio for exclusive photos',
    image_results: [{ passed: false }, { passed: false }],
    text_results: { score: 25 },
  }),
  makeProfile({
    id: 'demo-l-10',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'diego_r',
    name: 'Diego Reyes',
    age: 34,
    sex: 'Man',
    occupation: 'Civil engineer',
    location: 'Denver, CO',
    trust_score: 95,
    status: 'pending',
    hoursAgo: 0.55,
    images: [img('diego-1'), img('diego-2'), img('diego-3')],
    bio: 'Civil engineer who hikes fourteeners on weekends. Looking for someone adventurous.',
    prompts: [
      { question: 'Best trip I have taken', answer: 'Patagonia — ten days of backpacking with no cell service.' },
      { question: 'I am looking for', answer: 'Honesty, humor, and someone who likes the outdoors.' },
    ],
    image_results: [{ passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 97 },
  }),
  makeProfile({
    id: 'demo-l-11',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'hana_w',
    name: 'Hana Watanabe',
    age: 28,
    sex: 'Woman',
    occupation: 'UX researcher',
    location: 'Seattle, WA',
    trust_score: 70,
    status: 'pending',
    hoursAgo: 0.9,
    images: [img('hana-1'), img('hana-2')],
    bio: 'UX researcher. Cat person. Always testing new coffee roasters.',
    prompts: [{ question: 'My simple pleasures', answer: 'Rainy days, used bookstores, and homemade ramen.' }],
    image_results: [{ passed: true }, { passed: true }],
    text_results: { score: 73 },
  }),
  makeProfile({
    id: 'demo-l-12',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'omar_h',
    name: 'Omar Hassan',
    age: 36,
    sex: 'Man',
    occupation: 'High school teacher',
    location: 'Detroit, MI',
    trust_score: 35,
    status: 'pending',
    hoursAgo: 1.4,
    images: [img('omar-1'), img('omar-2'), img('omar-3')],
    bio: 'Teacher and basketball coach. Family-oriented.',
    image_results: [{ passed: true }, { passed: false }, { passed: true }],
    text_results: { score: 68 },
  }),
  makeProfile({
    id: 'demo-l-13',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'elena_v',
    name: 'Elena Vasquez',
    age: 31,
    sex: 'Woman',
    occupation: 'Dental hygienist',
    location: 'Miami, FL',
    trust_score: 30,
    status: 'pending',
    hoursAgo: 1.8,
    images: [img('elena-1')],
    bio: 'Beach walks and salsa dancing.',
    image_results: [{ passed: false }],
    text_results: { score: 55 },
  }),
  makeProfile({
    id: 'demo-l-14',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'kevin_l',
    name: 'Kevin Liu',
    age: 25,
    sex: 'Man',
    occupation: 'Graduate student',
    location: 'Boston, MA',
    trust_score: 48,
    status: 'pending',
    hoursAgo: 2.1,
    images: [img('kevin-1'), img('kevin-2')],
    bio: 'PhD candidate in neuroscience. Board game nights every Thursday.',
    text_results: { score: 52 },
  }),
  makeProfile({
    id: 'demo-l-15',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'amara_o',
    name: 'Amara Okafor',
    age: 33,
    sex: 'Woman',
    occupation: 'Marketing director',
    location: 'Atlanta, GA',
    trust_score: 84,
    status: 'pending',
    hoursAgo: 2.8,
    images: [img('amara-1'), img('amara-2'), img('amara-3'), img('amara-4')],
    bio: 'Marketing lead who volunteers at animal shelters on weekends.',
    prompts: [
      { question: 'Together we could', answer: 'Try every taco spot in the city.' },
      { question: 'A life goal of mine', answer: 'Open a community arts space.' },
    ],
    image_results: [{ passed: true }, { passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 86 },
  }),
  makeProfile({
    id: 'demo-l-16',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'noah_b',
    name: 'Noah Bennett',
    age: 42,
    sex: 'Man',
    occupation: 'Financial analyst',
    location: 'Charlotte, NC',
    trust_score: 76,
    status: 'approved',
    hoursAgo: 56,
    images: [img('noah-1'), img('noah-2')],
    bio: 'Dad of two. Golf on Sundays, grilling on Saturdays.',
    image_results: [{ passed: true }, { passed: true }],
    text_results: { score: 78 },
  }),
  makeProfile({
    id: 'demo-l-17',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'zoe_k',
    name: 'Zoe Kim',
    age: 27,
    sex: 'Woman',
    occupation: 'Physical therapist',
    location: 'Portland, OR',
    trust_score: 89,
    status: 'approved',
    hoursAgo: 64,
    images: [img('zoe-1'), img('zoe-2'), img('zoe-3')],
    bio: 'PT by day, pottery by night. Dog named Mochi.',
    image_results: [{ passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 91 },
  }),
  makeProfile({
    id: 'demo-l-18',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'marcus_j',
    name: 'Marcus Johnson',
    age: 38,
    sex: 'Man',
    occupation: 'Firefighter',
    location: 'Phoenix, AZ',
    trust_score: 93,
    status: 'approved',
    hoursAgo: 80,
    images: [img('marcus-1'), img('marcus-2')],
    bio: 'Firefighter and EMT. Cooking is my love language.',
    text_results: { score: 92 },
  }),
  makeProfile({
    id: 'demo-l-19',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'lucia_m',
    name: 'Lucia Moretti',
    age: 29,
    sex: 'Woman',
    occupation: 'Sommelier',
    location: 'Napa, CA',
    trust_score: 80,
    status: 'approved',
    hoursAgo: 96,
    images: [img('lucia-1'), img('lucia-2'), img('lucia-3')],
    bio: 'Wine professional. Pasta from scratch every Sunday.',
    image_results: [{ passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 83 },
  }),
  makeProfile({
    id: 'demo-l-20',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'spam_link_02',
    trust_score: 9,
    status: 'rejected',
    hoursAgo: 8,
    images: [img('spam-1')],
    bio: '💰 Make $5000/week from home! Link in bio 👇',
    image_results: [{ passed: false }],
    text_results: { score: 7 },
  }),
  makeProfile({
    id: 'demo-l-21',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'ai_gen_33',
    trust_score: 17,
    status: 'rejected',
    hoursAgo: 14,
    images: [img('ai-gen-1'), img('ai-gen-2'), img('ai-gen-3')],
    bio: 'Model. Travel influencer.',
    image_results: [{ passed: false }, { passed: false }, { passed: false }],
    text_results: { score: 22 },
  }),
  makeProfile({
    id: 'demo-l-22',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'catfish_88',
    trust_score: 26,
    status: 'rejected',
    hoursAgo: 18,
    images: [img('catfish-1')],
    bio: 'Send gift cards to verify you are real 💕',
    text_results: { score: 19 },
  }),
  makeProfile({
    id: 'demo-l-23',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'test_user_01',
    name: 'Jamie Park',
    age: 24,
    sex: 'Non-binary',
    occupation: 'Illustrator',
    location: 'Austin, TX',
    trust_score: 72,
    status: 'pending',
    hoursAgo: 0.7,
    images: [img('jamie-1'), img('jamie-2')],
    bio: 'Freelance illustrator. Skateboards and thrift stores.',
    image_results: [{ passed: true }, { passed: true }],
    text_results: { score: 74 },
  }),
  makeProfile({
    id: 'demo-l-24',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'test_user_02',
    name: 'Rachel Stein',
    age: 30,
    sex: 'Woman',
    occupation: 'Attorney',
    location: 'Washington, DC',
    trust_score: 87,
    status: 'approved',
    hoursAgo: 120,
    images: [img('rachel-1'), img('rachel-2')],
    bio: 'Public interest lawyer. Rowing team on the Potomac.',
    text_results: { score: 85 },
  }),
  makeProfile({
    id: 'demo-l-25',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'test_minimal',
    trust_score: 44,
    status: 'pending',
    hoursAgo: 3.5,
    images: [img('minimal-1')],
    bio: 'Hi',
    text_results: { score: 38 },
  }),
  makeProfile({
    id: 'demo-l-26',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'staging_reject',
    trust_score: 15,
    status: 'rejected',
    hoursAgo: 20,
    images: [img('staging-rej-1')],
    bio: 'WhatsApp me at +1-555-0199 for verification',
    image_results: [{ passed: false }],
    text_results: { score: 11 },
  }),
];

const fraudProfiles: DemoProfile[] = [
  makeProfile({
    id: 'demo-f-1',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'unknown_882',
    trust_score: 19,
    status: 'pending',
    hoursAgo: 0.2,
    images: [img('fraud-1')],
    bio: 'Investor. DM for crypto tips.',
    image_results: [{ passed: false }],
    text_results: { score: 15 },
  }),
  makeProfile({
    id: 'demo-f-2',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'lisa_real?',
    trust_score: 41,
    status: 'pending',
    hoursAgo: 0.6,
    images: [img('fraud-2'), img('fraud-3')],
    bio: 'Model. LA based.',
    image_results: [{ passed: true }, { passed: false }],
    text_results: { score: 38 },
  }),
  makeProfile({
    id: 'demo-f-3',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'marcus_t',
    trust_score: 28,
    status: 'pending',
    hoursAgo: 1,
    images: [img('fraud-4')],
    bio: 'Entrepreneur. 6 figs month.',
    text_results: { score: 31 },
  }),
  makeProfile({
    id: 'demo-f-4',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'duplicate_face_7',
    trust_score: 33,
    status: 'pending',
    hoursAgo: 1.5,
    images: [img('fraud-5'), img('fraud-6')],
    bio: 'Travel addict ✈️',
    image_results: [{ passed: false }, { passed: false }],
    text_results: { score: 40 },
  }),
  makeProfile({
    id: 'demo-f-5',
    api_key_id: 'demo-key-eu',
    profile_user_id: 'emma_berlin',
    name: 'Emma Schneider',
    age: 33,
    sex: 'Woman',
    occupation: 'Architect',
    location: 'Berlin, DE',
    trust_score: 76,
    status: 'pending',
    hoursAgo: 2,
    images: [img('emma-1'), img('emma-2')],
    bio: 'Architect in Berlin. Museums and cycling.',
    image_results: [{ passed: true }, { passed: true }],
    text_results: { score: 80 },
  }),
  makeProfile({
    id: 'demo-f-6',
    api_key_id: 'demo-key-eu',
    profile_user_id: 'pierre_l',
    trust_score: 85,
    status: 'approved',
    hoursAgo: 36,
    images: [img('pierre-1'), img('pierre-2'), img('pierre-3')],
    bio: 'Chef. Wine nerd.',
    text_results: { score: 87 },
  }),
  makeProfile({
    id: 'demo-f-7',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'scam_batch_12',
    trust_score: 8,
    status: 'rejected',
    hoursAgo: 4,
    images: [img('scam-1')],
    bio: 'Free iPhone giveaway click here',
    text_results: { score: 9 },
  }),
  makeProfile({
    id: 'demo-f-8',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'ai_face_99',
    trust_score: 14,
    status: 'rejected',
    hoursAgo: 8,
    images: [img('ai-1'), img('ai-2')],
    image_results: [{ passed: false }, { passed: false }],
    text_results: { score: 20 },
  }),
  makeProfile({
    id: 'demo-f-9',
    api_key_id: 'demo-key-eu',
    profile_user_id: 'sofia_m',
    trust_score: 11,
    status: 'rejected',
    hoursAgo: 10,
    images: [img('sofia-1')],
    bio: 'Only fans link below',
    text_results: { score: 12 },
  }),
  makeProfile({
    id: 'demo-f-10',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'crypto_bro_77',
    trust_score: 6,
    status: 'pending',
    hoursAgo: 0.05,
    images: [img('crypto-1')],
    bio: 'Bitcoin millionaire. I will teach you my strategy for 10% fee.',
    image_results: [{ passed: false }],
    text_results: { score: 5 },
  }),
  makeProfile({
    id: 'demo-f-11',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'stock_photo_12',
    name: 'Jessica',
    age: 25,
    sex: 'Woman',
    trust_score: 32,
    status: 'pending',
    hoursAgo: 0.3,
    images: [img('stock-1'), img('stock-2'), img('stock-3')],
    bio: 'Love to travel ✈️🌴',
    image_results: [{ passed: false }, { passed: false }, { passed: true }],
    text_results: { score: 45 },
  }),
  makeProfile({
    id: 'demo-f-12',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'mismatch_id_4',
    name: 'David Chen',
    age: 40,
    sex: 'Man',
    occupation: 'Doctor',
    location: 'New York, NY',
    trust_score: 39,
    status: 'pending',
    hoursAgo: 0.45,
    images: [img('mismatch-1'), img('mismatch-2')],
    bio: 'Cardiologist at Mount Sinai. Widowed, looking for companionship.',
    image_results: [{ passed: false }, { passed: false }],
    text_results: { score: 72 },
  }),
  makeProfile({
    id: 'demo-f-13',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'romance_scam_3',
    trust_score: 21,
    status: 'pending',
    hoursAgo: 0.55,
    images: [img('romance-1')],
    bio: 'Oil rig worker. Need iTunes gift card to fix satellite phone.',
    text_results: { score: 16 },
  }),
  makeProfile({
    id: 'demo-f-14',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'batch_signup_91',
    trust_score: 13,
    status: 'pending',
    hoursAgo: 0.65,
    images: [img('batch-1')],
    bio: 'Hey beautiful 😘',
    image_results: [{ passed: false }],
    text_results: { score: 10 },
  }),
  makeProfile({
    id: 'demo-f-15',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'duplicate_face_22',
    trust_score: 29,
    status: 'pending',
    hoursAgo: 0.75,
    images: [img('dup-1'), img('dup-2'), img('dup-3')],
    bio: 'Fitness coach. DM for coaching.',
    image_results: [{ passed: false }, { passed: false }, { passed: false }],
    text_results: { score: 34 },
  }),
  makeProfile({
    id: 'demo-f-16',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'thin_profile_8',
    trust_score: 43,
    status: 'pending',
    hoursAgo: 0.85,
    images: [img('thin-1')],
    bio: 'New account',
    text_results: { score: 41 },
  }),
  makeProfile({
    id: 'demo-f-17',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'borderline_ok_1',
    name: 'Thomas Wright',
    age: 35,
    sex: 'Man',
    occupation: 'Accountant',
    location: 'Dallas, TX',
    trust_score: 42,
    status: 'pending',
    hoursAgo: 1.1,
    images: [img('thomas-1'), img('thomas-2')],
    bio: 'CPA. Cowboys fan. Looking for something real.',
    image_results: [{ passed: true }, { passed: false }],
    text_results: { score: 58 },
  }),
  makeProfile({
    id: 'demo-f-18',
    api_key_id: 'demo-key-eu',
    profile_user_id: 'lars_h',
    name: 'Lars Hendriks',
    age: 37,
    sex: 'Man',
    occupation: 'Product manager',
    location: 'Amsterdam, NL',
    trust_score: 83,
    status: 'pending',
    hoursAgo: 1.3,
    images: [img('lars-1'), img('lars-2')],
    bio: 'Cycling to work year-round. Craft beer enthusiast.',
    image_results: [{ passed: true }, { passed: true }],
    text_results: { score: 85 },
  }),
  makeProfile({
    id: 'demo-f-19',
    api_key_id: 'demo-key-eu',
    profile_user_id: 'chiara_r',
    name: 'Chiara Romano',
    age: 30,
    sex: 'Woman',
    occupation: 'Fashion buyer',
    location: 'Milan, IT',
    trust_score: 78,
    status: 'pending',
    hoursAgo: 1.6,
    images: [img('chiara-1'), img('chiara-2'), img('chiara-3')],
    bio: 'Fashion buyer. Aperitivo culture convert.',
    image_results: [{ passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 81 },
  }),
  makeProfile({
    id: 'demo-f-20',
    api_key_id: 'demo-key-eu',
    profile_user_id: 'approved_eu_1',
    name: 'Nadia Petrov',
    age: 32,
    sex: 'Woman',
    occupation: 'Data scientist',
    location: 'Warsaw, PL',
    trust_score: 88,
    status: 'approved',
    hoursAgo: 48,
    images: [img('nadia-1'), img('nadia-2')],
    bio: 'ML engineer. Cross-country skiing in winter.',
    text_results: { score: 90 },
  }),
  makeProfile({
    id: 'demo-f-21',
    api_key_id: 'demo-key-eu',
    profile_user_id: 'approved_eu_2',
    name: 'Henrik Sørensen',
    age: 41,
    sex: 'Man',
    occupation: 'Marine biologist',
    location: 'Copenhagen, DK',
    trust_score: 91,
    status: 'approved',
    hoursAgo: 60,
    images: [img('henrik-1'), img('henrik-2'), img('henrik-3')],
    bio: 'Research diver. Cooking Nordic cuisine at home.',
    image_results: [{ passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 93 },
  }),
  makeProfile({
    id: 'demo-f-22',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'phishing_44',
    trust_score: 4,
    status: 'rejected',
    hoursAgo: 3,
    images: [img('phish-1')],
    bio: 'Verify your account: bit.ly/suspicious-link',
    image_results: [{ passed: false }],
    text_results: { score: 3 },
  }),
  makeProfile({
    id: 'demo-f-23',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'nsfw_spam_9',
    trust_score: 7,
    status: 'rejected',
    hoursAgo: 6,
    images: [img('nsfw-1'), img('nsfw-2')],
    bio: '18+ content only. Subscribe now.',
    image_results: [{ passed: false }, { passed: false }],
    text_results: { score: 8 },
  }),
  makeProfile({
    id: 'demo-f-24',
    api_key_id: 'demo-key-eu',
    profile_user_id: 'rejected_eu_1',
    trust_score: 18,
    status: 'rejected',
    hoursAgo: 15,
    images: [img('rej-eu-1')],
    bio: 'Sugar daddy looking for arrangement 💸',
    text_results: { score: 14 },
  }),
];

const surgeProfiles: DemoProfile[] = [
  makeProfile({
    id: 'demo-s-1',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_001',
    trust_score: 74,
    status: 'pending',
    hoursAgo: 0.1,
    images: [img('s1-1'), img('s1-2')],
    bio: 'Just moved to Austin. Foodie.',
    text_results: { score: 78 },
  }),
  makeProfile({
    id: 'demo-s-2',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_002',
    name: 'Priya Nair',
    age: 26,
    sex: 'Woman',
    occupation: 'Yoga instructor',
    trust_score: 81,
    status: 'pending',
    hoursAgo: 0.15,
    images: [img('s2-1'), img('s2-2'), img('s2-3')],
    bio: 'Yoga instructor. Plant mom.',
    image_results: [{ passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 84 },
  }),
  makeProfile({
    id: 'demo-s-3',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_003',
    trust_score: 69,
    status: 'pending',
    hoursAgo: 0.2,
    images: [img('s3-1')],
    bio: 'Software PM. Board games on Fridays.',
    text_results: { score: 71 },
  }),
  makeProfile({
    id: 'demo-s-4',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_004',
    trust_score: 58,
    status: 'pending',
    hoursAgo: 0.25,
    images: [img('s4-1'), img('s4-2')],
    bio: 'Grad student. Coffee shops and concerts.',
    image_results: [{ passed: true }, { passed: false }],
    text_results: { score: 62 },
  }),
  makeProfile({
    id: 'demo-s-5',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_005',
    trust_score: 92,
    status: 'pending',
    hoursAgo: 0.3,
    images: [img('s5-1'), img('s5-2'), img('s5-3'), img('s5-4')],
    bio: 'Pediatrician. Marathon finisher.',
    text_results: { score: 95 },
  }),
  makeProfile({
    id: 'demo-s-6',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'weekend_006',
    trust_score: 47,
    status: 'pending',
    hoursAgo: 0.35,
    images: [img('s6-1')],
    bio: 'Artist. Selling prints in bio.',
    text_results: { score: 49 },
  }),
  makeProfile({
    id: 'demo-s-7',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'weekend_007',
    trust_score: 63,
    status: 'pending',
    hoursAgo: 0.4,
    images: [img('s7-1'), img('s7-2')],
    bio: 'Data scientist. Climbing gym regular.',
    text_results: { score: 66 },
  }),
  makeProfile({
    id: 'demo-s-8',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_008',
    trust_score: 86,
    status: 'approved',
    hoursAgo: 24,
    images: [img('s8-1'), img('s8-2')],
    bio: 'Veterinarian. Two cats, one opinion.',
    text_results: { score: 89 },
  }),
  makeProfile({
    id: 'demo-s-9',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_009',
    trust_score: 90,
    status: 'approved',
    hoursAgo: 30,
    images: [img('s9-1'), img('s9-2'), img('s9-3')],
    bio: 'Nonprofit lead. Community gardens.',
    text_results: { score: 91 },
  }),
  makeProfile({
    id: 'demo-s-10',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_010',
    trust_score: 77,
    status: 'approved',
    hoursAgo: 40,
    images: [img('s10-1'), img('s10-2')],
    bio: 'Journalist. Slow mornings, fast deadlines.',
    text_results: { score: 79 },
  }),
  makeProfile({
    id: 'demo-s-11',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'spam_weekend',
    trust_score: 16,
    status: 'rejected',
    hoursAgo: 5,
    images: [img('s11-1')],
    bio: 'Win cash now!!!',
    text_results: { score: 14 },
  }),
  makeProfile({
    id: 'demo-s-12',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'low_quality_3',
    trust_score: 24,
    status: 'rejected',
    hoursAgo: 7,
    images: [img('s12-1')],
    image_results: [{ passed: false }],
    text_results: { score: 27 },
  }),
  makeProfile({
    id: 'demo-s-13',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_013',
    name: 'Isabella Torres',
    age: 28,
    sex: 'Woman',
    occupation: 'Event planner',
    location: 'San Diego, CA',
    trust_score: 79,
    status: 'pending',
    hoursAgo: 0.12,
    images: [img('s13-1'), img('s13-2'), img('s13-3')],
    bio: 'Events by day, salsa by night.',
    image_results: [{ passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 82 },
  }),
  makeProfile({
    id: 'demo-s-14',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_014',
    name: 'Ben Carter',
    age: 32,
    sex: 'Man',
    occupation: 'Electrician',
    location: 'Nashville, TN',
    trust_score: 71,
    status: 'pending',
    hoursAgo: 0.18,
    images: [img('s14-1'), img('s14-2')],
    bio: 'Trade worker who plays guitar at open mics.',
    text_results: { score: 74 },
  }),
  makeProfile({
    id: 'demo-s-15',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_015',
    name: 'Yuki Tanaka',
    age: 26,
    sex: 'Woman',
    occupation: 'Game developer',
    location: 'San Francisco, CA',
    trust_score: 65,
    status: 'pending',
    hoursAgo: 0.22,
    images: [img('s15-1'), img('s15-2')],
    bio: 'Indie game dev. Ramen connoisseur.',
    image_results: [{ passed: true }, { passed: false }],
    text_results: { score: 68 },
  }),
  makeProfile({
    id: 'demo-s-16',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_016',
    trust_score: 52,
    status: 'pending',
    hoursAgo: 0.28,
    images: [img('s16-1')],
    bio: 'Just relocated. Exploring the city.',
    text_results: { score: 48 },
  }),
  makeProfile({
    id: 'demo-s-17',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_017',
    name: 'Fatima Al-Rashid',
    age: 34,
    sex: 'Woman',
    occupation: 'Pharmacist',
    location: 'Houston, TX',
    trust_score: 88,
    status: 'pending',
    hoursAgo: 0.32,
    images: [img('s17-1'), img('s17-2'), img('s17-3')],
    bio: 'Pharmacist and marathon runner. Family is everything.',
    prompts: [{ question: 'I value', answer: 'Faith, kindness, and good food.' }],
    image_results: [{ passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 90 },
  }),
  makeProfile({
    id: 'demo-s-18',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_018',
    name: 'Ethan Brooks',
    age: 29,
    sex: 'Man',
    occupation: 'Real estate agent',
    location: 'Tampa, FL',
    trust_score: 56,
    status: 'pending',
    hoursAgo: 0.38,
    images: [img('s18-1'), img('s18-2')],
    bio: 'Realtor. Beach volleyball every Saturday.',
    image_results: [{ passed: true }, { passed: true }],
    text_results: { score: 59 },
  }),
  makeProfile({
    id: 'demo-s-19',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_019',
    trust_score: 37,
    status: 'pending',
    hoursAgo: 0.42,
    images: [img('s19-1'), img('s19-2')],
    bio: 'Entrepreneur. Passive income tips in bio.',
    image_results: [{ passed: false }, { passed: true }],
    text_results: { score: 33 },
  }),
  makeProfile({
    id: 'demo-s-20',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_020',
    name: 'Grace O\'Brien',
    age: 31,
    sex: 'Woman',
    occupation: 'Librarian',
    location: 'Minneapolis, MN',
    trust_score: 83,
    status: 'pending',
    hoursAgo: 0.48,
    images: [img('s20-1'), img('s20-2')],
    bio: 'Public librarian. Book club host and bread baker.',
    text_results: { score: 86 },
  }),
  makeProfile({
    id: 'demo-s-21',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_021',
    name: 'Andre Williams',
    age: 45,
    sex: 'Man',
    occupation: 'Chef',
    location: 'New Orleans, LA',
    trust_score: 94,
    status: 'pending',
    hoursAgo: 0.52,
    images: [img('s21-1'), img('s21-2'), img('s21-3'), img('s21-4')],
    bio: 'Executive chef. Jazz clubs and gumbo.',
    image_results: [{ passed: true }, { passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 96 },
  }),
  makeProfile({
    id: 'demo-s-22',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_022',
    trust_score: 29,
    status: 'pending',
    hoursAgo: 0.58,
    images: [img('s22-1')],
    bio: 'Add my snap 👻',
    image_results: [{ passed: false }],
    text_results: { score: 21 },
  }),
  makeProfile({
    id: 'demo-s-23',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'weekend_023',
    name: 'Sofia Andersson',
    age: 27,
    sex: 'Woman',
    occupation: 'Biomedical engineer',
    location: 'Stockholm, SE',
    trust_score: 75,
    status: 'pending',
    hoursAgo: 0.62,
    images: [img('s23-1'), img('s23-2')],
    bio: 'Medtech startup. CrossFit and fika breaks.',
    text_results: { score: 77 },
  }),
  makeProfile({
    id: 'demo-s-24',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'weekend_024',
    name: 'Carlos Mendez',
    age: 33,
    sex: 'Man',
    occupation: 'Architect',
    location: 'Mexico City, MX',
    trust_score: 68,
    status: 'pending',
    hoursAgo: 0.68,
    images: [img('s24-1'), img('s24-2'), img('s24-3')],
    bio: 'Designing sustainable housing. Street food explorer.',
    image_results: [{ passed: true }, { passed: true }, { passed: false }],
    text_results: { score: 70 },
  }),
  makeProfile({
    id: 'demo-s-25',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'weekend_025',
    trust_score: 41,
    status: 'pending',
    hoursAgo: 0.72,
    images: [img('s25-1')],
    bio: 'Influencer. Collab? DM rates.',
    text_results: { score: 36 },
  }),
  makeProfile({
    id: 'demo-s-26',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_026',
    name: 'Megan Foster',
    age: 36,
    sex: 'Woman',
    occupation: 'Nurse practitioner',
    location: 'Cleveland, OH',
    trust_score: 85,
    status: 'approved',
    hoursAgo: 28,
    images: [img('s26-1'), img('s26-2')],
    bio: 'NP in urgent care. Running and true crime podcasts.',
    text_results: { score: 87 },
  }),
  makeProfile({
    id: 'demo-s-27',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_027',
    name: 'James Okonkwo',
    age: 39,
    sex: 'Man',
    occupation: 'Attorney',
    location: 'Philadelphia, PA',
    trust_score: 82,
    status: 'approved',
    hoursAgo: 36,
    images: [img('s27-1'), img('s27-2'), img('s27-3')],
    bio: 'Immigration lawyer. Soccer coach on weekends.',
    image_results: [{ passed: true }, { passed: true }, { passed: true }],
    text_results: { score: 84 },
  }),
  makeProfile({
    id: 'demo-s-28',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_028',
    name: 'Aisha Rahman',
    age: 28,
    sex: 'Woman',
    occupation: 'Software engineer',
    location: 'Toronto, ON',
    trust_score: 78,
    status: 'approved',
    hoursAgo: 44,
    images: [img('s28-1'), img('s28-2')],
    bio: 'Backend engineer. Hiking the Bruce Trail.',
    text_results: { score: 80 },
  }),
  makeProfile({
    id: 'demo-s-29',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'weekend_029',
    name: 'Tyler Nguyen',
    age: 23,
    sex: 'Man',
    occupation: 'Barista',
    location: 'Portland, OR',
    trust_score: 61,
    status: 'approved',
    hoursAgo: 52,
    images: [img('s29-1'), img('s29-2')],
    bio: 'Coffee nerd. Vinyl collector.',
    text_results: { score: 64 },
  }),
  makeProfile({
    id: 'demo-s-30',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_030',
    trust_score: 11,
    status: 'rejected',
    hoursAgo: 4,
    images: [img('s30-1')],
    bio: 'You won a prize! Enter credit card to claim.',
    text_results: { score: 8 },
  }),
  makeProfile({
    id: 'demo-s-31',
    api_key_id: 'demo-key-prod',
    profile_user_id: 'weekend_031',
    trust_score: 19,
    status: 'rejected',
    hoursAgo: 9,
    images: [img('s31-1'), img('s31-2')],
    bio: 'AI generated photos only. No video calls.',
    image_results: [{ passed: false }, { passed: false }],
    text_results: { score: 15 },
  }),
  makeProfile({
    id: 'demo-s-32',
    api_key_id: 'demo-key-staging',
    profile_user_id: 'weekend_032',
    trust_score: 23,
    status: 'rejected',
    hoursAgo: 11,
    images: [img('s32-1')],
    bio: 'Looking for generous men only',
    text_results: { score: 18 },
  }),
];

export const DEMO_SCENARIOS: DemoScenario[] = [
  {
    id: 'launch-week',
    label: 'Launch week queue',
    description: '26 profiles across production and staging — high-trust signups, borderline cases, scams, and mixed image/text results.',
    apiKeys: [
      { id: 'demo-key-prod', title: 'Production', key_prefix: 'nv_live_••••8f2a' },
      { id: 'demo-key-staging', title: 'Staging', key_prefix: 'nv_test_••••41c9' },
    ],
    defaultApiKeyId: 'demo-key-prod',
    thresholdSettings: { auto_approve_threshold: 75, auto_reject_threshold: 35 },
    webhookUrl: 'https://api.example.com/hooks/moderation',
    imageSettings: {
      global_configuration: { face_detect: true, ai_image_detect: true },
      per_image_configuration: {},
      image_block: false,
    },
    profiles: launchProfiles,
  },
  {
    id: 'fraud-spike',
    label: 'Fraud spike review',
    description: '24 profiles with a fraud-heavy pending queue — AI faces, romance scams, stock photos, plus legitimate EU signups.',
    apiKeys: [
      { id: 'demo-key-prod', title: 'Production', key_prefix: 'nv_live_••••8f2a' },
      { id: 'demo-key-eu', title: 'EU rollout', key_prefix: 'nv_live_••••euro' },
    ],
    defaultApiKeyId: 'demo-key-prod',
    thresholdSettings: { auto_approve_threshold: 70, auto_reject_threshold: 40 },
    webhookUrl: null,
    imageSettings: {
      global_configuration: { face_detect: true, ai_image_detect: true },
      per_image_configuration: { '0': { ai_image_detect: true } },
      image_block: true,
    },
    profiles: fraudProfiles,
  },
  {
    id: 'weekend-surge',
    label: 'Weekend signup surge',
    description: '32 profiles simulating a busy weekend — dense incoming queue with varied scores, demographics, and rejection reasons.',
    apiKeys: [
      { id: 'demo-key-prod', title: 'Production', key_prefix: 'nv_live_••••8f2a' },
      { id: 'demo-key-staging', title: 'Staging', key_prefix: 'nv_test_••••41c9' },
    ],
    defaultApiKeyId: 'demo-key-prod',
    thresholdSettings: { auto_approve_threshold: 80, auto_reject_threshold: 30 },
    webhookUrl: 'https://hooks.acme.social/novembr',
    imageSettings: {
      global_configuration: { face_detect: true, ai_image_detect: false },
      per_image_configuration: {},
      image_block: false,
    },
    profiles: surgeProfiles,
  },
];

export const getScenarioById = (id: string) => DEMO_SCENARIOS.find((s) => s.id === id) ?? DEMO_SCENARIOS[0];

export const splitProfilesByStatus = (profiles: DemoProfile[], apiKeyId: string) => {
  const filtered = profiles.filter((p) => p.api_key_id === apiKeyId);
  return {
    pending: filtered.filter((p) => p.status === 'pending'),
    approved: filtered.filter((p) => p.status === 'approved'),
    rejected: filtered.filter((p) => p.status === 'rejected'),
  };
};
